import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { ApiError, type WsEslesme } from './api';
import { TarihAraligiSecici } from './TarihSecici';
import { WS } from './metin/web-sitesi';
import { vurguParcalari } from './web-sitesi-bicim';

/**
 * Web sitesi bölümünün ortak parçaları: veri kancası, tek arama kutusu, tarih aralığı, tablo,
 * boş durum, grafik çerçevesi, ipucu ve vurgu.
 */

// ---------------------------------------------------------------------------
// Veri
// ---------------------------------------------------------------------------

export interface Veri<T> {
  veri: T | null;
  hata: string | null;
  yukleniyor: boolean;
  yenile: () => void;
}

/**
 * Kaynak değişince yeniden yükler ve **eski veriyi tutar**: arama yazarken liste boşalıp
 * yeniden dolmuyor, yeni sonuç gelince yer değiştiriyor. Yarış korumalı: geç gelen eski yanıt
 * yeni yanıtın üstüne yazmaz.
 */
export function useVeri<T>(
  yukle: () => Promise<T>,
  bagimlilik: readonly unknown[],
): Veri<T> {
  const [durum, setDurum] = useState<{ veri: T | null; hata: string | null; yukleniyor: boolean }>({ veri: null, hata: null, yukleniyor: true });
  const sayac = useRef(0);
  const [tazele, setTazele] = useState(0);
  useEffect(() => {
    const no = ++sayac.current;
    setDurum((o) => ({ ...o, yukleniyor: true }));
    yukle().then(
      (v) => {
        if (no === sayac.current) setDurum({ veri: v, hata: null, yukleniyor: false });
      },
      (e: unknown) => {
        if (no === sayac.current) setDurum((o) => ({ veri: o.veri, hata: e instanceof ApiError ? e.message : WS.hata, yukleniyor: false }));
      },
    );
  }, [...bagimlilik, tazele]);
  return { ...durum, yenile: useCallback(() => setTazele((n) => n + 1), []) };
}

// ---------------------------------------------------------------------------
// Vurgu
// ---------------------------------------------------------------------------

export function Vurgu({ metin, eslesme, alan }: { metin: string; eslesme?: WsEslesme[]; alan: string }) {
  const e = eslesme?.find((x) => x.alan === alan);
  const [once, orta, sonra] = vurguParcalari(metin, e?.aralik);
  if (!orta) return <>{metin}</>;
  return (
    <>
      {once}
      <mark>{orta}</mark>
      {sonra}
    </>
  );
}

/** Satırda yazı olarak görünmeyen bir alanda eşleşme olduysa küçük bir etiket: ne yüzden bulundu. */
export function EslesmeEtiketi({ eslesme, gorunen }: { eslesme?: WsEslesme[]; gorunen: readonly string[] }) {
  const gizli = (eslesme ?? []).filter((e) => !gorunen.includes(e.alan));
  if (gizli.length === 0) return null;
  const adlar = [...new Set(gizli.map((e) => (WS.alan as Record<string, string>)[e.alan] ?? e.alan))];
  return (
    <span className="eslesti" data-testid="ws-eslesti">
      {WS.ara.eslesti}: {adlar.join(', ')}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Arama ve tarih aralığı
// ---------------------------------------------------------------------------

/** Her listenin tek arama kutusu ve tarih aralığı. Yazı 250 ms durulunca sorguya iniyor. */
export function AramaAlani({
  q,
  setQ,
  from,
  to,
  setAralik,
  placeholder,
  testId,
}: {
  q: string;
  setQ: (v: string) => void;
  from: string;
  to: string;
  setAralik: (from: string, to: string) => void;
  placeholder: string;
  testId: string;
}) {
  const [yerel, setYerel] = useState(q);
  const son = useRef(q);
  useEffect(() => {
    if (q !== son.current) {
      son.current = q;
      setYerel(q);
    }
  }, [q]);
  useEffect(() => {
    if (yerel === son.current) return undefined;
    const t = setTimeout(() => {
      son.current = yerel;
      setQ(yerel);
    }, 250);
    return () => clearTimeout(t);
  }, [yerel, setQ]);
  return (
    <>
      <input
        className="search"
        type="search"
        placeholder={placeholder}
        aria-label={WS.ara.aria}
        title={WS.ara.ipucu}
        value={yerel}
        data-testid={`${testId}-ara`}
        onChange={(e) => setYerel(e.target.value)}
      />
      <span className="aralik-alani">
        <TarihAraligiSecici testId={`${testId}-aralik`} giris={from} cikis={to} setAralik={setAralik} bosMetin={WS.ara.aralikBos} yarimMetin={WS.ara.aralikYari} geceGoster={false} />
        {from || to ? (
          <button type="button" className="link-btn" data-testid={`${testId}-aralik-temizle`} onClick={() => setAralik('', '')}>
            {WS.ara.aralikTemizle}
          </button>
        ) : null}
      </span>
    </>
  );
}

// ---------------------------------------------------------------------------
// Tablo, boş durum
// ---------------------------------------------------------------------------

export interface SatirOzeti {
  key: string;
  gec?: boolean;
  hucreler: ReactNode[];
  ac: () => void;
  testId?: string;
}

export function Dt({ kolonlar, satirlar, sinif, testId }: { kolonlar: string[]; satirlar: SatirOzeti[]; sinif?: string; testId?: string }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className={`dt ${sinif ?? ''}`} data-testid={testId}>
        <thead>
          <tr>
            {kolonlar.map((k) => (
              <th key={k}>{k}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {satirlar.map((s) => (
            <tr
              key={s.key}
              className={s.gec ? 'rl' : undefined}
              tabIndex={0}
              data-testid={s.testId}
              onClick={(e) => {
                if ((e.target as HTMLElement).closest('button,a,input,select')) return;
                s.ac();
              }}
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
                  e.preventDefault();
                  s.ac();
                }
              }}
            >
              {s.hucreler.map((h, i) => (
                <td key={kolonlar[i] ?? i} data-label={kolonlar[i]} className={i === 0 ? 'first' : undefined}>
                  {h}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Bos({ baslik, alt, dugme }: { baslik: string; alt: string; dugme?: ReactNode }) {
  return (
    <div className="empty-state" data-testid="ws-bos">
      <b>{baslik}</b>
      {alt}
      {dugme ? <div style={{ marginTop: 12 }}>{dugme}</div> : null}
    </div>
  );
}

export function Hata({ mesaj, yenile }: { mesaj: string; yenile: () => void }) {
  return (
    <div className="empty-state" role="alert" data-testid="ws-hata">
      <b>{WS.hata}</b>
      {mesaj}
      <div style={{ marginTop: 12 }}>
        <button type="button" className="btn sm" onClick={yenile}>
          {WS.yenile}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Grafik çerçevesi
// ---------------------------------------------------------------------------

/** Çocuğa kendi genişliğini veriyor: SVG'ler ekrana göre yeniden çiziliyor. */
export function Olcekli({ children }: { children: (genislik: number) => ReactNode }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [w, setW] = useState(600);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const ol = () => setW(Math.max(240, Math.floor(el.clientWidth || 600)));
    ol();
    const ro = new ResizeObserver(ol);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div className="viz-body" ref={ref}>
      {children(w)}
    </div>
  );
}

/** Grafik kartı: başlık, açıklama ve "tablo görünümü" geçişi (grafiğin okunabilir ikizi). */
export function VizKart({
  id,
  baslik,
  alt,
  sinif,
  ekstra,
  grafik,
  tablo,
  altEk,
}: {
  id: string;
  baslik: string;
  alt: string;
  sinif?: string;
  ekstra?: ReactNode;
  grafik: (genislik: number) => ReactNode;
  tablo: () => ReactNode;
  altEk?: ReactNode;
}) {
  const [tabloda, setTabloda] = useState(false);
  return (
    <section className={`card ${sinif ?? ''}`} id={id} data-testid={id}>
      <div className="card-head">
        <div>
          <h3>{baslik}</h3>
          <p className="sub">{alt}</p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-sm)', alignItems: 'center' }}>
          {ekstra}
          <button type="button" className="link-btn" aria-pressed={tabloda} onClick={() => setTabloda((t) => !t)}>
            {tabloda ? WS.grafik : WS.tablo}
          </button>
        </div>
      </div>
      {tabloda ? <div className="viz-body">{tablo()}</div> : <Olcekli>{grafik}</Olcekli>}
      {altEk}
    </section>
  );
}

export function OlcumYok({ anahtarlar }: { anahtarlar: ('src' | 'ulke' | 'yanit' | 'zi' | 'ts')[] }) {
  return (
    <div className="nomeasure">
      {anahtarlar.map((k) => (
        <div className="nm" key={k}>
          <b>{WS.olcumYok[k][0]}</b>
          <span>{WS.olcumYok[k][1]}</span>
          <em>{WS.olcumYok.ayrac}</em>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// İpucu ve bildirim
// ---------------------------------------------------------------------------

/**
 * `data-tip` taşıyan her şeyin üstüne gelince ya da odaklanınca küçük ipucu. Yeni satır `\n`;
 * `white-space: pre-line` ile çiziliyor, yani metin HTML olarak yorumlanmıyor.
 */
export function useIpucu() {
  const [tip, setTip] = useState<{ metin: string; x: number; y: number; alta: boolean } | null>(null);
  const kutu = useRef<HTMLDivElement | null>(null);
  const goster = useCallback((e: { target: EventTarget | null }) => {
    const hedef = (e.target as HTMLElement | null)?.closest?.('[data-tip]');
    if (!hedef) return;
    const r = hedef.getBoundingClientRect();
    setTip({ metin: hedef.getAttribute('data-tip') ?? '', x: r.left + r.width / 2, y: r.top, alta: false });
  }, []);
  const gizle = useCallback(() => setTip(null), []);
  useLayoutEffect(() => {
    const el = kutu.current;
    if (!tip || !el) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const x = Math.max(8, Math.min(window.innerWidth - w - 8, tip.x - w / 2));
    const ustte = tip.y - h - 8;
    el.style.left = `${x}px`;
    el.style.top = `${ustte < 8 ? tip.y + 24 : ustte}px`;
  }, [tip]);
  const katman = (
    <div ref={kutu} className={tip ? 'tip on' : 'tip'} role="tooltip" style={{ whiteSpace: 'pre-line' }}>
      {tip?.metin}
    </div>
  );
  return { katman, ozellikler: { onPointerOver: goster, onPointerOut: gizle, onFocus: goster, onBlur: gizle } };
}

export function Bildirim({ metin }: { metin: string | null }) {
  return (
    <div className={metin ? 'toast on' : 'toast'} role="status">
      {metin}
    </div>
  );
}
